import { Box, FormControl, FormHelperText, Grid, MenuItem, Paper, Select, Tooltip, WithStyles } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import * as React from "react";
import Typography from "@material-ui/core/Typography/Typography";
import { styles } from "../ProductDetailsHistoricalBox.styles";
import { BlueButton } from "../../../components/Buttons/BlueButton";
import { useEffect, useState } from "react";
import {
    getBaseCurrencies,
    getExchanges,
    getFilterableSubscriptionFilesInfo,
    getFilterableSubscriptionPricingPackages
} from "../../../../stores/filterableProducts/kaiko/KaikoAction";
import { createFilterableSubscriptionRequest } from "../../../../stores/filterableSubscriptionRequests/FilterableSubscriptionRequestAction";
import * as UserDetailsAction from "../../../../stores/user/UserDetailsAction";
import { useDispatch, useSelector } from "react-redux";
import { convertDateStringToNumber, isDateInPastMonths, isDateMonthValid, today } from "../../../../utilities/dateTimeUtils";
import {
    dateInPastMonthError,
    dateMonthInvalidError,
    isMaxBaseCurrenciesReached,
    isNothingSelected,
    isNoUserSelected,
    maxSelectionError,
    noSelectionError,
    noUserSelectedError
} from "../helpers/productDetailsHelper";
import { AutocompleteWithCheckboxes } from "../../../components/FormComponents/AutocompleteWithCheckboxes";
import { DATE_FORMAT } from "../../../../constants/TIME";
import UserDetailsModel from "../../../../models/user/UserDetailsModel";
import { AutocompleteUserDropdown } from "../../../components/FormComponents/AutocompleteUserDropdown";
import { useAuthContext } from "../../../../auth/AuthProvider";
import PermissionGuard from "../../../components/PermissionGuard/PermissionGuard";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isAdminUser, isLoggedIn, isShopManagerUser } from "../../../../utilities/userUtils";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../../constants/ToastStatusEnum";
import { FormDateMonthField } from "../../../components/FormComponents/FormDateMonthField";
import { KaikoPricingInfoDialog } from "./KaikoPricingInfoDialog";
import { formatPrice } from "../../../../utilities/PriceUtils";

const KAIKO_MAX_BASE_CURRENCIES = 100;
const REFERENCE_DATE_OFFSET = 5;

export interface IKaikoItem {
    id: string;
    code: string;
    name: string;
}

export interface ISubscriptionType {
    id: number;
    name: string;
}

export interface ISubscriptionBoxProps extends WithStyles<typeof styles> {
    product: string;
    isAlreadySubscribed: boolean;
    subscriptionTypes: ISubscriptionType[];
}

const KaikoSubscriptionBoxPure: React.FunctionComponent<ISubscriptionBoxProps> = ({
    product,
    isAlreadySubscribed,
    subscriptionTypes,
    classes
}: ISubscriptionBoxProps) => {
    const dispatch = useDispatch();
    const authContext = useAuthContext();
    const history = useHistory();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const isAdmin = isShopManagerUser(authContext) || isAdminUser(authContext);
    const isUserLogged = isLoggedIn(authContext);

    const [selectedBaseCurrencies, setSelectedBaseCurrencies] = useState<IKaikoItem[]>([]);
    const [from, setFrom] = useState<string>(today().startOf("month").format(DATE_FORMAT));
    const [subscriptionType, setSubscriptionType] = useState<number>(subscriptionTypes[0].id);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(isAdmin ? null : authContext?.user?.id);


    const kaikoSubscriptionPricingPackages = useSelector(state => state.kaiko?.subscriptionPricingPackages);
    const allKaikoBaseCurrencies = useSelector(state => state.kaiko?.levelTwo);
    const kaikoSubscriptionFilesInfo = useSelector(state => state.kaiko?.subscriptionFilesInfo);

    const allUsersFromState = useSelector(state => state.userDetails?.all);

    let allUsers;
    if (allUsersFromState) {
        allUsers = Object.values(allUsersFromState)?.map((user: UserDetailsModel) => ({
            userId: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email
        }));
    }

    useEffect(() => {
        if (isAdmin) dispatch(UserDetailsAction.requestAllUsersDetails());

        dispatch(getFilterableSubscriptionPricingPackages(subscriptionType));
        if (!isDateInPastMonths(from)) {
            const referenceDate = today().subtract(REFERENCE_DATE_OFFSET, "days").format(DATE_FORMAT);

            const params = {
                from: convertDateStringToNumber(referenceDate),
                to: convertDateStringToNumber(referenceDate)
            };
            dispatch(getBaseCurrencies(product, params));
        }
        // eslint-disable-next-line
    }, [subscriptionType]);

    useEffect(() => {
        if (
            !isNothingSelected(selectedBaseCurrencies.length) &&
            !isMaxBaseCurrenciesReached(selectedBaseCurrencies.length, KAIKO_MAX_BASE_CURRENCIES)
        ) {
            const params = {
                levelOne: null,
                levelTwo: selectedBaseCurrencies.map(i => i.code)?.join(","),
                from: convertDateStringToNumber(from)
            };
            dispatch(getFilterableSubscriptionFilesInfo(subscriptionType, params));
        }
        // eslint-disable-next-line
    }, [selectedBaseCurrencies, subscriptionType]);

    const handleDateChange = from => {
        setFrom(from);
        if (!isDateMonthValid(from))
            return;
        const params = {
            from: convertDateStringToNumber(from),
            to: convertDateStringToNumber(from)
        };

        if (!isDateInPastMonths(from)) {
            dispatch(getExchanges(product, params));
        }
        setSelectedBaseCurrencies([]);
    };

    const handleBaseCurrenciesChange = (baseCurrencies: IKaikoItem[]) => {
        setSelectedBaseCurrencies(baseCurrencies);
    };

    const handleSubscriptionTypeChange = (event: any) => {
        setSubscriptionType(event.target.value);
    };

    const handleSendRequest = () => {
        const parameters = {
            subscriptionTypeId: subscriptionType,
            startDate: convertDateStringToNumber(from),
            baseCurrencies: selectedBaseCurrencies
        };
        dispatch(createFilterableSubscriptionRequest(selectedUserId, parameters))
            .then(() => {
                enqueueSnackbar("Subscription request sent", { variant: ToastStatusEnum.Info });
                if (!isAdmin)
                    history.push("/subscription-request/success");
            })
            .catch(e => {
                enqueueSnackbar(e.message, { variant: ToastStatusEnum.Error });
            });
    };

    const handleUserChange = userId => {
        if (userId) setSelectedUserId(userId?.userId);
        else setSelectedUserId(null);
    };

    const handleGoToSubscriptions = () => {
        history.push("/my-account/my-subscriptions");
    };

    return (
        <>
            <Grid item xs={12} key="historical-box-kaiko">
                <Paper elevation={0} className={classes.paper}>
                    <Box margin={4} className={classes.kaikoCard}>
                        {isUserLogged ? (
                            <div>
                                <Typography variant="h2">Subscription</Typography>
                                <FormControl style={{ width: "100%" }}>
                                    <div className={classes.sectionInput}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={4}>
                                                <Typography variant={"caption"}>Desired effective date</Typography>
                                                <FormDateMonthField
                                                    disabled={false}
                                                    placeholder="From"
                                                    value={from}
                                                    firstAvailableDate={today().format(DATE_FORMAT)}
                                                    onChange={value => handleDateChange(value)}
                                                    allowFuture
                                                />
                                            </Grid>
                                        </Grid>

                                        <Grid container spacing={10}>
                                            <Grid item xs={8}>
                                                <Typography variant={"caption"}>Subscription type</Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Select
                                                labelId="orderType"
                                                id="ordertype"
                                                name="type"
                                                value={subscriptionType}
                                                onChange={handleSubscriptionTypeChange}
                                                variant="outlined"
                                            >
                                                {subscriptionTypes.map(sT => (
                                                    <MenuItem key={sT.id} value={sT.id}>
                                                        {sT.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                        <Grid container spacing={10} className={classes.smallMarginTop}>
                                            <Grid item xs={8}>
                                                <Typography variant={"caption"}>Assets</Typography>
                                            </Grid>
                                        </Grid>

                                        <AutocompleteWithCheckboxes
                                            id="kaiko-base-currencies"
                                            allItems={allKaikoBaseCurrencies || []}
                                            selectedItems={selectedBaseCurrencies}
                                            onChange={newValue => handleBaseCurrenciesChange(newValue)}
                                        ></AutocompleteWithCheckboxes>
                                        <FormHelperText>
                                            The selected assets might not be available for the complete subscription
                                            date range.
                                        </FormHelperText>
                                        <PermissionGuard scope={["subscriptions.write"]}>
                                            <div>
                                                {isAdmin && (
                                                    <Grid container spacing={10} className={classes.smallMarginTop}>
                                                        <Grid item xs={8}>
                                                            <Typography variant={"caption"}>User</Typography>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                                {isAdmin && allUsers && (
                                                    <AutocompleteUserDropdown
                                                        allUsers={allUsers}
                                                        selectedUserId={selectedUserId}
                                                        handleUserChange={handleUserChange}
                                                    />
                                                )}
                                            </div>
                                        </PermissionGuard>
                                    </div>
                                    {selectedBaseCurrencies.length !== 0 &&
                                    kaikoSubscriptionFilesInfo &&
                                    kaikoSubscriptionFilesInfo?.isAnyUnavailable ? (
                                        <>
                                            <Typography>
                                                Not all exchange/asset combinations are available for the chosen date
                                                range.
                                            </Typography>
                                        </>
                                    ) : null}
                                </FormControl>
                                <div className={classes.buttonCheckout}>
                                    {selectedBaseCurrencies.length > 0 && kaikoSubscriptionPricingPackages && (
                                        <div className={classes.pricingDetailsSection}>
                                            <Grid container spacing={2} className={classes.marginTop}>
                                                <Grid item xs={7}>
                                                <Typography className={classes.titleName} variant="body2">
                                                    Product name
                                                </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography className={classes.price} variant="body2">
                                                        <b>{kaikoSubscriptionFilesInfo?.package?.name}</b>
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography className={classes.titleName} variant="body2">
                                                        Licence fee per month 
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography className={classes.price} variant="body2">
                                                        <b>{formatPrice(kaikoSubscriptionFilesInfo?.totalPrice)}</b>
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={1} className={classes.alignRight}>
                                                    <Tooltip title="Price details">
                                                        <span className={classes.price}>
                                                            <KaikoPricingInfoDialog packages={kaikoSubscriptionPricingPackages.packages} isSubscription={true}/>
                                                        </span>
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                        </div>
                                    )}
                                    <Grid container spacing={2}>
                                        <Grid item xs={isAlreadySubscribed ? 6 : 12}>
                                            <BlueButton
                                                disabled={
                                                    isDateInPastMonths(from) ||
                                                    !isDateMonthValid(from) ||
                                                    isNothingSelected(selectedBaseCurrencies.length) ||
                                                    isMaxBaseCurrenciesReached(
                                                        selectedBaseCurrencies.length,
                                                        KAIKO_MAX_BASE_CURRENCIES
                                                    ) ||
                                                    isNoUserSelected(selectedUserId)
                                                }
                                                fullWidth
                                                onClick={handleSendRequest}
                                            >
                                                Request subscription
                                            </BlueButton>
                                        </Grid>
                                        {isAlreadySubscribed && (
                                            <Grid item xs={6}>
                                                <BlueButton fullWidth onClick={handleGoToSubscriptions}>
                                                    Go to subscription
                                                </BlueButton>
                                            </Grid>
                                        )}
                                    </Grid>
                                    {/* {remark !== "" ? <FormHelperText error={true}>{remark}</FormHelperText> : null} */}
                                    {dateMonthInvalidError(from) ?? dateInPastMonthError(from) ??
                                        noSelectionError(selectedBaseCurrencies.length) ??
                                        maxSelectionError(selectedBaseCurrencies.length, KAIKO_MAX_BASE_CURRENCIES) ??
                                        noUserSelectedError(selectedUserId)}
                                </div>
                            </div>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <p>
                                        Creating a subscription request for this product is only available for
                                        registered users. Please log in or register.
                                    </p>
                                </Grid>
                                <Grid container item spacing={2}>
                                    <Grid item xs={2}>
                                        <BlueButton onClick={() => authContext.requestAuth()}>
                                            {t("signInOrRegister.logIn")}
                                        </BlueButton>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <BlueButton onClick={() => history.push("/register")}>
                                            {t("general.register")}
                                        </BlueButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                </Paper>
            </Grid>
        </>
    );
};

export const KaikoProductDetailsSubscriptionBox = withStyles(styles)(KaikoSubscriptionBoxPure);
